import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { METROS, METRO_NAMES } from "@/lib/metros";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Upload, MapPin, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const COUNTIES = [
  "Bexar","Comal","Guadalupe","Kendall",
  "Travis","Williamson","Hays","Bastrop","Caldwell",
  "Harris","Fort Bend","Montgomery","Brazoria","Galveston",
  "Dallas","Tarrant","Collin","Denton","Rockwall",
];
const TYPES = ["single_family","duplex","triplex","fourplex","multifamily","condo","townhouse","land"];

export default function Properties() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [metro, setMetro] = useState("All Metros");
  const [open, setOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [form, setForm] = useState({
    address:"", city:"San Antonio", county:"Bexar", zip_code:"", property_type:"single_family",
    bedrooms:3, bathrooms:2, sqft:0, year_built:0, zoning:"", acquisition_cost:0, conversion_cost:0,
    monthly_conventional_rent:0, monthly_specialized_rent:0, accessibility:"", notes:"",
  });

  const load = async () => setItems((await api.get("/properties")).data);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.address.trim()) return toast.error("Address required");
    await api.post("/properties", {
      ...form,
      bedrooms: +form.bedrooms, bathrooms: +form.bathrooms, sqft: +form.sqft, year_built: +form.year_built,
      acquisition_cost: +form.acquisition_cost, conversion_cost: +form.conversion_cost,
      monthly_conventional_rent: +form.monthly_conventional_rent, monthly_specialized_rent: +form.monthly_specialized_rent,
    });
    toast.success("Property added"); setOpen(false); load();
  };

  const importCsv = async () => {
    const lines = csv.trim().split("\n");
    if (lines.length < 2) return toast.error("Need header + at least one row");
    const headers = lines[0].split(",").map(h=>h.trim());
    const rows = lines.slice(1).map(l => {
      const cols = l.split(",");
      const obj = {}; headers.forEach((h,i) => obj[h] = cols[i]?.trim() || "");
      ["bedrooms","bathrooms","sqft","year_built","acquisition_cost","conversion_cost","monthly_conventional_rent","monthly_specialized_rent"]
        .forEach(k => { if (obj[k]!==undefined) obj[k] = Number(obj[k])||0; });
      return obj;
    });
    const r = await api.post("/properties/import", { rows });
    toast.success(`Imported ${r.data.created} properties`);
    setCsvOpen(false); setCsv(""); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Vetted Registry</div>
          <h1 className="text-3xl font-bold">Vetted Properties</h1>
          <p className="text-xs text-slate-500 mt-1">Properties enter intentionally through team intake — not through automated MLS scraping.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="import-csv-btn"><Upload className="w-4 h-4 mr-1"/>Import CSV</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Bulk import (CSV)</DialogTitle></DialogHeader>
              <p className="text-xs text-slate-500">Header row required. Columns: address, city, county, zip_code, property_type, bedrooms, bathrooms, sqft, year_built, zoning, acquisition_cost, conversion_cost, monthly_conventional_rent, monthly_specialized_rent</p>
              <Textarea rows={10} value={csv} onChange={e=>setCsv(e.target.value)} className="font-mono text-xs" placeholder="address,city,county,bedrooms,bathrooms,acquisition_cost,monthly_specialized_rent&#10;123 Main St,San Antonio,Bexar,3,2,250000,2800" />
              <Button onClick={importCsv} className="bg-slate-900 hover:bg-slate-800" data-testid="csv-submit">Import</Button>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-property-btn"><Plus className="w-4 h-4 mr-1"/>Add Property</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New Vetted Property</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Address</Label><Input data-testid="pr-address" value={form.address} onChange={e=>setForm({...form, address:e.target.value})}/></div>
                <div><Label>City</Label><Input value={form.city} onChange={e=>setForm({...form, city:e.target.value})}/></div>
                <div><Label>ZIP</Label><Input value={form.zip_code} onChange={e=>setForm({...form, zip_code:e.target.value})}/></div>
                <div><Label>County</Label>
                  <Select value={form.county} onValueChange={v=>setForm({...form, county:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{COUNTIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Type</Label>
                  <Select value={form.property_type} onValueChange={v=>setForm({...form, property_type:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t} className="capitalize">{t.replace("_"," ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Bedrooms</Label><Input type="number" value={form.bedrooms} onChange={e=>setForm({...form, bedrooms:e.target.value})}/></div>
                <div><Label>Bathrooms</Label><Input type="number" step="0.5" value={form.bathrooms} onChange={e=>setForm({...form, bathrooms:e.target.value})}/></div>
                <div><Label>Sqft</Label><Input type="number" value={form.sqft} onChange={e=>setForm({...form, sqft:e.target.value})}/></div>
                <div><Label>Year Built</Label><Input type="number" value={form.year_built} onChange={e=>setForm({...form, year_built:e.target.value})}/></div>
                <div><Label>Zoning</Label><Input value={form.zoning} onChange={e=>setForm({...form, zoning:e.target.value})}/></div>
                <div><Label>Acquisition Cost</Label><Input type="number" value={form.acquisition_cost} onChange={e=>setForm({...form, acquisition_cost:e.target.value})}/></div>
                <div><Label>Conversion Cost</Label><Input type="number" value={form.conversion_cost} onChange={e=>setForm({...form, conversion_cost:e.target.value})}/></div>
                <div><Label>Conventional Rent (mo)</Label><Input type="number" value={form.monthly_conventional_rent} onChange={e=>setForm({...form, monthly_conventional_rent:e.target.value})}/></div>
                <div><Label>Specialized Rent (mo)</Label><Input type="number" value={form.monthly_specialized_rent} onChange={e=>setForm({...form, monthly_specialized_rent:e.target.value})}/></div>
                <div className="col-span-2"><Label>Accessibility Notes</Label><Input value={form.accessibility} onChange={e=>setForm({...form, accessibility:e.target.value})}/></div>
                <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
              </div>
              <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="pr-submit">Save Property</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="p-4 border-slate-200 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by address, city, ZIP, notes..." className="pl-9" value={q} onChange={e=>setQ(e.target.value)} data-testid="prop-search"/>
        </div>
        <Select value={metro} onValueChange={setMetro}>
          <SelectTrigger className="w-56" data-testid="prop-metro-filter"><SelectValue placeholder="Metro"/></SelectTrigger>
          <SelectContent>{METRO_NAMES.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <div className="text-xs text-slate-500 font-mono">
          {items.filter(p => (metro==="All Metros" || (METROS[metro]||[]).includes(p.county)) && (q==="" || `${p.address} ${p.city} ${p.zip_code||""} ${p.notes||""}`.toLowerCase().includes(q.toLowerCase()))).length} shown
        </div>
      </Card>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.filter(p => (metro==="All Metros" || (METROS[metro]||[]).includes(p.county)) && (q==="" || `${p.address} ${p.city} ${p.zip_code||""} ${p.notes||""}`.toLowerCase().includes(q.toLowerCase()))).map(p => (
          <Link to={`/properties/${p.id}`} key={p.id} data-testid={`property-card-${p.id}`}>
            <Card className="p-5 border-slate-200 hover:border-slate-900 transition-colors h-full">
              {(p.photos && p.photos.length > 0) && (
                <div className="-m-5 mb-4 aspect-[16/9] bg-slate-100 overflow-hidden rounded-t-lg border-b border-slate-200">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.photos[0].storage_path}?auth=${encodeURIComponent(localStorage.getItem("sahoi_token") || "")}`}
                    alt={p.address}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex justify-between items-start mb-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{p.property_type?.replace("_"," ")}</div>
                <span className="text-[10px] font-semibold text-slate-700 flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.county}</span>
              </div>
              <h3 className="font-semibold text-slate-900">{p.address}</h3>
              <div className="text-xs text-slate-500">{p.city}, TX {p.zip_code}</div>
              <div className="text-xs text-slate-600 mt-2 flex gap-3">
                <span>{p.bedrooms}BR</span><span>{p.bathrooms}BA</span><span className="font-mono">{p.sqft} sf</span>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 text-xs">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Acquisition</div><div className="font-mono font-semibold">${Number(p.acquisition_cost||0).toLocaleString()}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Spec Rent/mo</div><div className="font-mono font-semibold text-[#C25E00]">${Number(p.monthly_specialized_rent||0).toLocaleString()}</div></div>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={(e)=>{e.preventDefault();e.stopPropagation();window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${p.address}, ${p.city}, TX${p.zip_code ? " " + p.zip_code : ""}`.trim())}`, "_blank", "noreferrer");}}
                  data-testid={`card-maps-${p.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider border border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
                >
                  <MapPin className="w-3 h-3" /> Google Maps
                </button>
                <button
                  onClick={(e)=>{e.preventDefault();e.stopPropagation();window.open(`https://www.zillow.com/homes/${encodeURIComponent(`${p.address}, ${p.city}, TX${p.zip_code ? " " + p.zip_code : ""}`.trim())}_rb/`, "_blank", "noreferrer");}}
                  data-testid={`card-zillow-${p.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider border border-[#006AFF] bg-[#006AFF] text-white hover:bg-[#0056cc]"
                >
                  <ExternalLink className="w-3 h-3" /> Zillow
                </button>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
